import React, { useState, useEffect } from "react";
import {Dialog,DialogTitle,DialogContent,DialogActions,DialogContentText,Button,TextField,Select,MenuItem,FormControl,FormControlLabel,InputLabel,Switch,Box,IconButton,InputAdornment,Tooltip,Typography} from "@mui/material";
import { Add, Delete, Lock } from "@mui/icons-material";
import { formatDate, addDays, toLocalDay } from "../utils/dateUtils";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { createGroup, updateGroup } from "../redux/ducks/group";
import { setTagAssignments } from "../redux/ducks/tag";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsLocked } from "../utils/lockUtils";
import { showAlert } from "../redux/slices/alerts";
import { triggerRefresh } from "../redux/slices/refreshTriggers";
import AppDatePicker from "./AppDatePicker";
import TagPicker, { tagSourceNames, useTagDraft } from "./TagPicker";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

const FormField = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
}));

const HalfWidthField = styled(FormField)(({ theme }) => ({
    width: "50%",
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(4),
}));

const PaymentItem = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
}));

const PaymentFields = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(2),
    flex: 1,
}));

const EmptyPaymentsHint = styled(Typography)({
    fontStyle: 'italic',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
}));

const BillingEntityNote = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
}));

const IntroText = styled(DialogContentText)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const defaultBillingTerms = { type: "Parent", billingEntity: false, frequency: "Monthly", hours: "", charge: "", overage: false };

const CreateGroupDialog = ({ open, onClose, accountId, accountData, groupId = null, editMode = false, groupData: initialGroupData = null }) => {
    const dispatch = useDispatch();
    const today = new Date();
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const canEditBillingTerms = hasPermission("editBillingTerms");
    const canEditBilling = canSeeInternal && canEditBillingTerms;
    const isLocked = useIsLocked(accountData, accountData?.client);
    const [editGroupId, setEditGroupId] = useState(groupId);
    const [payments, setPayments] = useState([]);
    const tagTargetId = editMode ? initialGroupData?._id || editGroupId || null : null;
    const tagsReadOnly = editMode && !!initialGroupData?.completedDate;
    const tagClientId = accountData?.client?._id || accountData?.client || null;
    const groupTags = editMode ? initialGroupData?.tags : null;
    const tagDraft = useTagDraft({
        enabled: open && (!editMode || (!!tagTargetId && !tagsReadOnly && Array.isArray(groupTags))),
        targetType: tagTargetId ? "Group" : undefined,
        targetId: tagTargetId,
        parentType: "Account",
        parentId: accountId,
        tags: groupTags,
        prefillInherited: !!initialGroupData?.tagsStale,
    });
    const tagSources = tagSourceNames(accountData, accountData?.client);
    const [isSaving, setIsSaving] = useState(false);
    const [groupData, setGroupData] = useState({
        name: "",
        priority: "Medium",
        privacy: "Public",
        billingTerms: { ...defaultBillingTerms },
        startDate: toLocalDay(today),
        dueDate: toLocalDay(addDays(today, 30)),
        projects: [],
        isDeleted: false,
        shouldRestore: false,
        budget: {
            budgeted: 0,
            quoted: 0,
        },
    });

    useEffect(() => {
        if (initialGroupData) {
            setGroupData({
                name: initialGroupData?.name,
                priority: initialGroupData?.priority,
                privacy: initialGroupData?.privacy,
                billingTerms: { ...defaultBillingTerms, ...(initialGroupData?.billingTerms || {}) },
                startDate: formatDate(initialGroupData?.startDate),
                dueDate: formatDate(initialGroupData?.dueDate),
                projects: initialGroupData?.projects,
                isDeleted: initialGroupData?.isDeleted,
                shouldRestore: initialGroupData?.shouldRestore,
                budget: {
                    budgeted: initialGroupData?.budget?.budgeted,
                    quoted: initialGroupData?.budget?.quoted,
                },
            });
            if (initialGroupData?.payments && Array.isArray(initialGroupData.payments)) {
                setPayments(initialGroupData.payments.map(p => ({
                    paymentNumber: p.paymentNumber,
                    value: p.value || "",
                    date: p.date ? formatDate(p.date) : "",
                })));
            }
        }
    }, [initialGroupData]);

    useEffect(() => {
        if (groupId) {
            setEditGroupId(groupId);
            setGroupData((prevData) => ({ ...prevData, _id: groupId }));
        }
    }, [groupId]);


    const billingTerms = groupData.billingTerms || defaultBillingTerms;
    const billingType = billingTerms.type || "Parent";
    const isRetainer = billingType === "Retainer";
    const isFixedPrice = billingType === "Fixed Price";
    const forcesBillingEntity = isRetainer || isFixedPrice;
    const readyToSubmit = (groupData.name || "").trim().length >= 2;

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if (name === "budgeted" || name === "quoted") {
            setGroupData((prevData) => ({
                ...prevData,
                budget: {
                    ...prevData.budget,
                    [name]: parseFloat(value) || 0,
                },
            }));
        } else {
            setGroupData((prevData) => ({ ...prevData, [name]: value }));
        }
    };

    const handleBillingTermChange = (field, value) => {
        setGroupData((prevData) => ({ ...prevData, billingTerms: { ...prevData.billingTerms, [field]: value } }));
    };

    const handleBillingTypeChange = (event) => {
        const type = event.target.value;
        handleBillingTermChange("type", type);
        if (type !== "Fixed Price") {
            setPayments([]);
        }
    };

    const handleAddPayment = () => {
        const maxPaymentNumber = payments.length > 0 
            ? Math.max(...payments.map(p => p.paymentNumber || 0))
            : 0;
        setPayments([...payments, {
            paymentNumber: maxPaymentNumber + 1,
            value: "",
            date: "",
        }]);
    };

    const handleRemovePayment = (index) => {
        setPayments(payments.filter((_, i) => i !== index));
    };

    const handlePaymentChange = (index, field, value) => {
        const updated = [...payments];
        updated[index] = { ...updated[index], [field]: value };
        setPayments(updated);
    };

    const handleSubmit = () => {
        if (isLocked) return;

        const termsToSend = {
            ...billingTerms,
            type: billingType,
            hours: billingTerms.hours ? parseInt(billingTerms.hours, 10) : 0,
            charge: billingTerms.charge ? parseFloat(billingTerms.charge) : 0,
            overage: billingTerms.overage === "true" || billingTerms.overage === true,
            billingEntity: forcesBillingEntity ? true : billingTerms.billingEntity === "true" || billingTerms.billingEntity === true
        };

        if (isRetainer && (!termsToSend.hours || !termsToSend.charge)) {
            dispatch(showAlert({ severity: "error", message: "A retainer needs both the hours included each period and the amount charged for them." }));
            return;
        }

        const apiData = {
            name: groupData.name,
            priority: groupData.priority,
            privacy: groupData.privacy,
            billingTerms: termsToSend,
            startDate: groupData.startDate,
            dueDate: groupData.dueDate,
            projects: groupData.projects,
            isDeleted: groupData.isDeleted,
            shouldRestore: groupData.shouldRestore,
            budget: {
                budgeted: groupData.budget.budgeted,
                quoted: groupData.budget.quoted,
            },
            account: accountId,
        };

        if (isFixedPrice && payments.length > 0) {
            // Validate all payments have value > 0
            const invalidPayments = payments.filter(p => !p.value || parseFloat(p.value) <= 0);
            if (invalidPayments.length > 0) {
                dispatch(showAlert({ severity: 'error', message: 'All payments must have a value greater than $0.00' }));
                return;
            }

            // Calculate total and percentages
            const total = payments.reduce((acc, p) => acc + (parseFloat(p.value) || 0), 0);
            const formattedPayments = payments.map(payment => ({
                paymentNumber: payment.paymentNumber,
                status: 'Pending',
                percentage: total > 0 ? ((parseFloat(payment.value) || 0) / total) * 100 : 0,
                value: parseFloat(payment.value) || 0,
                date: payment.date && payment.date !== '' ? new Date(payment.date) : null
            }));
            apiData.payments = formattedPayments;
            apiData.totalPayment = total;
        }

        if (editMode && editGroupId) {
            const saveTags = tagDraft.dirty && !tagsReadOnly && tagTargetId && Array.isArray(groupTags);
            setIsSaving(true);
            dispatch(
                updateGroup(editGroupId, apiData, (response) => {
                    if (!(response && response.group)) {
                        setIsSaving(false);
                        if (response) dispatch(showAlert({ severity: 'error', message: 'Failed to update group' }));
                        return;
                    }
                    const finish = () => {
                        setIsSaving(false);
                        dispatch(showAlert({ severity: "success", message: "Group updated successfully" }));
                        dispatch(triggerRefresh({ triggerName: "groups" }));
                        onClose();
                    };
                    if (!saveTags) {
                        finish();
                        return;
                    }
                    dispatch(setTagAssignments("Group", tagTargetId, tagDraft.assignments, (data) => {
                        if (data) {
                            finish();
                            return;
                        }
                        setIsSaving(false);
                        dispatch(triggerRefresh({ triggerName: "groups" }));
                    }));
                })
            );
        } else {
            Object.assign(apiData, tagDraft.assignments);
            dispatch(
                createGroup(apiData, (response) => {
                    if (response && response.group) {
                        dispatch(showAlert({ severity: "success", message: "Group created successfully" }));
                        dispatch(triggerRefresh({ triggerName: "groups" }));
                        onClose();
                    } else if (response) {
                        dispatch(showAlert({ severity: 'error', message: 'Failed to create group' }));
                    }
                })
            );
        }
    };

    return (
        <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{editMode ? "Edit Group" : "Create Group"}</DialogTitle>
            <StyledDialogContent>
                <IntroText variant="body2">
                    A group collects related projects under {accountData?.name || "this account"}. Saving {editMode ? "applies your changes to the group" : "creates the group"} and applies the
                    billing terms below to every project inside it.
                </IntroText>
                <FormField>
                    <TextField fullWidth label="Name" name="name" value={groupData.name} onChange={handleInputChange} required />
                </FormField>

                <FormField>
                    <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select name="priority" value={groupData.priority} onChange={handleInputChange} label="Priority">
                            <MenuItem value="Lowest">Lowest</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Highest">Highest</MenuItem>
                        </Select>
                    </FormControl>
                </FormField>

                <FormField>
                    {tagsReadOnly ? (
                        <TagPicker value={Array.isArray(groupTags) ? groupTags : []} targetType="Group" targetId={tagTargetId} sourceNames={tagSources} readOnly lockedReason="Completed work can’t be retagged" />
                    ) : (
                        <TagPicker
                            draft={tagDraft}
                            targetType={tagTargetId ? "Group" : undefined}
                            targetId={tagTargetId}
                            parentType="Account"
                            parentId={accountId}
                            clientId={tagClientId}
                            clientName={accountData?.client?.name}
                            accountId={accountId}
                            sourceNames={tagSources}
                            disabled={editMode && !Array.isArray(groupTags)}
                        />
                    )}
                </FormField>

                <Row>
                    <HalfWidthField>
                        <AppDatePicker
                            label="Start Date"
                            name="startDate"
                            value={groupData.startDate}
                            onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                        />
                    </HalfWidthField>
                    <HalfWidthField>
                        <AppDatePicker
                            label="Due Date"
                            name="dueDate"
                            value={groupData.dueDate}
                            onChange={(v, n) => handleInputChange({ target: { name: n, value: v } })}
                        />
                    </HalfWidthField>
                </Row>

                {canSeeInternal && (
                    <>
                        <Row>
                            <HalfWidthField>
                                <FormControl fullWidth>
                                    <InputLabel>Privacy</InputLabel>
                                    <Select name="privacy" value={groupData.privacy} onChange={handleInputChange} label="Privacy">
                                        <MenuItem value="Public">Public</MenuItem>
                                        <MenuItem value="Internal">Internal</MenuItem>
                                        <MenuItem value="Private">Private</MenuItem>
                                    </Select>
                                </FormControl>
                            </HalfWidthField>

                            {canEditBillingTerms && (
                                <HalfWidthField>
                                    <FormControl fullWidth>
                                        <InputLabel>Billing Type</InputLabel>
                                        <Select value={billingType} onChange={handleBillingTypeChange} label="Billing Type">
                                            <MenuItem value="Parent">Parent</MenuItem>
                                            <MenuItem value="Retainer">Retainer</MenuItem>
                                            <MenuItem value="Hourly">Hourly</MenuItem>
                                            <MenuItem value="Fixed Price">Fixed Price</MenuItem>
                                            <MenuItem value="Internal">Internal</MenuItem>
                                            <MenuItem value="Non Billable">Non Billable</MenuItem>
                                        </Select>
                                    </FormControl>
                                </HalfWidthField>
                            )}
                        </Row>

                        {canEditBilling && isRetainer && (
                            <FormField>
                                <SectionTitle variant="subtitle1">Retainer Terms</SectionTitle>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    The client pays a set amount for a set number of hours each period. Work logged against this group draws down that pool.
                                </Typography>
                                <Row>
                                    <HalfWidthField>
                                        <FormControl fullWidth>
                                            <InputLabel>Billing Frequency</InputLabel>
                                            <Select
                                                value={billingTerms.frequency || "Monthly"}
                                                onChange={(e) => handleBillingTermChange("frequency", e.target.value)}
                                                label="Billing Frequency"
                                            >
                                                <MenuItem value="Monthly">Monthly</MenuItem>
                                                <MenuItem value="Weekly">Weekly</MenuItem>
                                                <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                                                <MenuItem value="Quarterly">Quarterly</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </HalfWidthField>
                                    <HalfWidthField>
                                        <TextField
                                            fullWidth
                                            label="Hours Included"
                                            type="number"
                                            value={billingTerms.hours ?? ""}
                                            onChange={(e) => handleBillingTermChange("hours", e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">hours</InputAdornment> }}
                                            inputProps={{ step: 1, min: 0 }}
                                            helperText="Hours the client has already paid for in each period."
                                        />
                                    </HalfWidthField>
                                </Row>
                                <Row>
                                    <HalfWidthField>
                                        <TextField
                                            fullWidth
                                            label="Charge Per Period"
                                            type="number"
                                            value={billingTerms.charge ?? ""}
                                            onChange={(e) => handleBillingTermChange("charge", e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                            inputProps={{ step: 0.01, min: 0 }}
                                            helperText="Amount invoiced each period for those hours."
                                        />
                                    </HalfWidthField>
                                    <HalfWidthField>
                                        <Tooltip title="On: hours worked past the included hours are added to the invoice at the normal rate. Off: extra hours are absorbed and not billed.">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={billingTerms.overage === "true" || billingTerms.overage === true}
                                                        onChange={(e) => handleBillingTermChange("overage", e.target.checked)}
                                                    />
                                                }
                                                label="Retainer Overage"
                                            />
                                        </Tooltip>
                                    </HalfWidthField>
                                </Row>
                            </FormField>
                        )}

                        {canEditBilling && isFixedPrice && (
                            <FormField>
                                <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                    <Typography variant="subtitle1">Payments</Typography>
                                    <Button
                                        startIcon={<Add />}
                                        onClick={handleAddPayment}
                                        size="small"
                                        variant="outlined"
                                    >
                                        Add Payment
                                    </Button>
                                </Box>
                                {payments.length > 0 ? (
                                    payments.map((payment, index) => (
                                        <PaymentItem key={index}>
                                            <PaymentFields>
                                                <TextField
                                                    fullWidth
                                                    label="Payment Value"
                                                    type="number"
                                                    value={payment.value || ''}
                                                    onChange={(e) => handlePaymentChange(index, 'value', e.target.value)}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                                                    }}
                                                    inputProps={{ step: 0.01, min: 0.01 }}
                                                    size="small"
                                                />
                                                <AppDatePicker
                                                    label="Due Date"
                                                    value={formatDate(payment.date)}
                                                    onChange={(v) => handlePaymentChange(index, 'date', v)}
                                                    size="small"
                                                />
                                            </PaymentFields>
                                            <IconButton
                                                onClick={() => handleRemovePayment(index)}
                                                color="error"
                                                size="small"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </PaymentItem>
                                    ))
                                ) : (
                                    <EmptyPaymentsHint variant="body2" color="text.secondary">
                                        No payments added. Click "Add Payment" to set up payment schedule.
                                    </EmptyPaymentsHint>
                                )}
                            </FormField>
                        )}
                    </>
                )}

                <Row>
                    {canSeeInternal ? (
                        <>
                            <HalfWidthField>
                                <TextField fullWidth label="Budgeted Time" value={groupData.budget.budgeted} name="budgeted" type="number" onChange={handleInputChange} />
                            </HalfWidthField>
                            <HalfWidthField>
                                <TextField fullWidth label="Quoted Time" value={groupData.budget.quoted} name="quoted" type="number" onChange={handleInputChange} />
                            </HalfWidthField>
                        </>
                    ) : (
                        <FormControl fullWidth>
                            <TextField fullWidth label="Approved Budget" value={groupData.budget.quoted} name="quoted" type="number" onChange={handleInputChange} />
                        </FormControl>
                    )}
                </Row>

            </StyledDialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!readyToSubmit || isLocked || isSaving || tagDraft.loading}>
                    {editMode ? "Update Group" : "Create Group"}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default CreateGroupDialog;
